import { WorkflowRole } from '@exam-management/common/types';
import {
  EntityType,
  useGetQuestionReviewTaskByIdQuery,
  useGetQuestionsByIdAndQuestionReviewTaskIdQuery,
  useGetTaskQuestionVerdictsQuery,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import {
  getWorkflowRoleForSubtask,
  getWorkflowRoleForTask,
} from '@exam-management/task-management';
import { useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import { useRefetchOnWindowFocusQuery } from '@shared/hooks';
import { groupBy } from '@shared/utils';
import { createContext, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { type Ability, getQuestionReviewTaskAbility, TaskType } from '../../utils';

import type {
  QuestionReviewTask,
  QuestionReviewTaskDetailsContextType,
  QuestionReviewTaskDetailsProviderProps,
} from './types';

export const QuestionReviewTaskDetailsContext = createContext<
  QuestionReviewTaskDetailsContextType | undefined
>(undefined);

export function QuestionReviewTaskDetailsProvider(
  props: Readonly<QuestionReviewTaskDetailsProviderProps>,
) {
  const { taskId, children } = props;
  const location = useLocation();

  const { data: GetTaskResult, loading: isGettingTaskResult } = useGetQuestionReviewTaskByIdQuery({
    variables: {
      input: {
        taskId,
      },
    },
    fetchPolicy: 'network-only',
  });

  const taskDetails = GetTaskResult?.wflGetTaskById.task;
  const isSubtask = !!taskDetails?.parentTask?.id;
  const questionIds =
    ((isSubtask ? taskDetails?.parentTask?.taskEntities : taskDetails?.taskEntities) ?? [])
      .filter((entity) => entity.entityType === EntityType.Question)
      .map((entity) => entity.entityId) ?? [];

  const { data: getQuestionsResult, loading: isGettingQuestionsResult } =
    useGetQuestionsByIdAndQuestionReviewTaskIdQuery({
      variables: {
        ids: questionIds,
        taskId,
      },
      fetchPolicy: 'network-only',
      skip: !questionIds?.length,
    });

  const { data: getTaskQuestionVerdictsResult, loading: isGettingTaskQuestionVerdictsResult } =
    useRefetchOnWindowFocusQuery(useGetTaskQuestionVerdictsQuery, {
      variables: {
        input: {
          taskId,
        },
      },
      fetchPolicy: 'network-only',
      skip: !taskId,
    });

  const questions = getQuestionsResult?.qctGetTaskQuestionsByIds ?? [];
  const taskQuestionVerdicts = getTaskQuestionVerdictsResult?.qctGetTaskQuestionVerdicts ?? [];

  const questionTaskQuestions = useMemo(() => {
    const verdictsByQuestionId = groupBy(taskQuestionVerdicts, (verdict) => verdict.entityId);

    return questions.map((question) => ({
      ...question,
      verdicts: verdictsByQuestionId[question.id] ?? [],
    }));
  }, [questions, taskQuestionVerdicts]);

  const { userProfile } = useAuth();
  const userId = userProfile?.id ?? '';

  const workflowRoles = useMemo(() => {
    if (isSubtask) {
      return getWorkflowRoleForSubtask({
        userId,
        assigneeId: taskDetails.assigneeId,
        assignerId: taskDetails.assignerId,
        parentOwnerId: taskDetails.parentTask?.ownerId ?? '',
      });
    }

    return getWorkflowRoleForTask({
      userId,
      assigneeId: taskDetails?.assigneeId ?? '',
      assignerId: taskDetails?.assignerId ?? '',
      ownerId: taskDetails?.ownerId ?? '',
    });
  }, [userProfile, taskDetails]);

  const checkUserAbility = (ability: `${Ability}`, subtaskId?: TaskType) => {
    if (subtaskId) {
      const subtask = taskDetails?.subtasks.find((subtask) => subtask.id === subtaskId);
      if (subtask) {
        const roles = getWorkflowRoleForSubtask({
          userId,
          assigneeId: subtask.assignee.id,
          assignerId: subtask.assigner.id,
          parentOwnerId: taskDetails?.ownerId ?? '',
        });

        return getQuestionReviewTaskAbility({
          workflowRoles: roles,
          status: subtask.currentWorkflowStep.code,
          ability: [ability],
          taskType: [TaskType.SUBTASK],
        });
      }
    }

    return getQuestionReviewTaskAbility({
      workflowRoles,
      status: taskDetails?.currentWorkflowStep.code ?? WorkflowStepCode.QuestionReviewTodo,
      ability: [ability],
      taskType: [isSubtask ? TaskType.SUBTASK : TaskType.TASK],
    });
  };

  const roles = {
    isOwner: workflowRoles.includes(WorkflowRole.TASK_OWNER),
    isAssigner: workflowRoles.includes(WorkflowRole.TASK_ASSIGNER),
    isAssignee: workflowRoles.includes(WorkflowRole.SUBTASK_ASSIGNEE),
  };

  const contextValues = useMemo(
    () => ({
      taskDetails: taskDetails as QuestionReviewTask,
      workflowRoles: roles,
      questions: questionTaskQuestions,
      checkUserAbility,
    }),
    [taskDetails, workflowRoles, questionTaskQuestions, checkUserAbility],
  );

  const isLoading =
    isGettingTaskResult || isGettingQuestionsResult || isGettingTaskQuestionVerdictsResult;

  if (isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  if (!taskDetails) {
    const isOnMyTaskRoute = location.pathname.includes('my-tasks');

    return <Navigate to={isOnMyTaskRoute ? '/' : '/task-management/question-review'} />;
  }

  return (
    <QuestionReviewTaskDetailsContext.Provider value={contextValues}>
      {children ?? <Outlet />}
    </QuestionReviewTaskDetailsContext.Provider>
  );
}
